<template>
    <div
        role="group"
        :aria-label="eligibleVisitorsLabel"
        class="eligible-visitors"
        :class="{ 'left-align': !hasRules || eligibleVisitors.error }">
        <div v-if="!hasRules">
            <div
                class="eligible-visitors-text">
                {{ $t('eligibleVisitorsList.Add rules to see eligible visitors.') }}
                <pendo-popover-v2
                    theme="dark"
                    arrow
                    placement="top"
                    :container="false"
                    is-tooltip>
                    <template #default>
                        <pendo-icon-button
                            icon="info"
                            icon-size="14"
                            size="small"
                            :aria-label="$t('eligibleVisitorsList.More information about eligible visitors')" />
                    </template>
                    <!-- eslint-disable vue/no-v-html -->
                    <template #popover>
                        <div
                            role="status"
                            aria-live="polite"
                            class="eligible-visitors-info-tooltip"
                            v-html="eligibleVisitorsTooltipMessage" />
                    </template>
                </pendo-popover-v2>
            </div>
            <div
                v-if="hasMinVisitorsRequirement"
                class="eligible-visitors-description">
                {{ $t('eligibleVisitorsList.Must have at least {minVisitorsInSegment} eligible visitors to view data and save segment.', { minVisitorsInSegment: minVisitorsInSegment }) }}
            </div>
        </div>
        <template v-else>
            <div class="eligible-visitors-filter-wrapper">
                <span
                    id="filter-by-label"
                    class="eligible-visitors-label">{{ $t('eligibleVisitorsList.Filter by') }}</span>
                <pendo-popover-v2
                    theme="dark"
                    arrow
                    placement="top"
                    :container="false"
                    is-tooltip>
                    <template #default>
                        <pendo-icon-button
                            icon="info"
                            icon-size="14"
                            size="small"
                            :aria-label="$t('eligibleVisitorsList.More information about filter by')" />
                    </template>
                    <template #popover>
                        <div
                            role="status"
                            aria-live="polite"
                            class="eligible-visitors-filter-tooltip">
                            {{ filterTooltipMessage }}
                        </div>
                    </template>
                </pendo-popover-v2>
            </div>
            <pendo-multiselect
                v-model="value"
                class="eligible-visitors-time-range-select"
                aria-labelledby="filter-by-label"
                :options="options"
                full-width />
            <div v-if="eligibleVisitors.error">
                <div class="eligible-visitors-text">
                    {{ $t('eligibleVisitorsList.An error occurred while fetching eligible visitors.') }}
                </div>
            </div>
            <div
                v-else-if="!eligibleVisitors.isLoading"
                class="eligible-visitors-wrapper">
                <div class="eligible-visitors-text bold">
                    {{ $t("eligibleVisitorsList.What's considered an eligible visitor?") }}
                    <pendo-popover-v2
                        theme="dark"
                        arrow
                        placement="top"
                        :container="false"
                        is-tooltip>
                        <template #default>
                            <pendo-icon-button
                                icon="info"
                                icon-size="14"
                                size="small"
                                :aria-label="$t('eligibleVisitorsList.More information about eligible visitors')" />
                        </template>
                        <!-- eslint-disable vue/no-v-html -->
                        <template #popover>
                            <div
                                role="status"
                                aria-live="polite"
                                class="eligible-visitors-info-tooltip"
                                v-html="eligibleVisitorsTooltipMessage" />
                        </template>
                    </pendo-popover-v2>
                </div>
                <div>
                    <div
                        v-if="analyticsDelayed || segment.analyticsDelayed"
                        class="eligible-visitors-analytics-delayed">
                        <pendo-alert type="warning">
                            <div>
                                {{
                                    $t(
                                        "eligibleVisitorsList.We're processing your CSV file. Eligible visitors appear as they are processed. Large segments may take several minutes to complete.",
                                    )
                                }}
                            </div>
                        </pendo-alert>
                    </div>
                    <div v-if="eligibleVisitors.count === 0">
                        {{ $t('eligibleVisitorsList.No visitors to show for this segment.') }}
                    </div>
                    <div
                        v-else-if="!showEligibleVisitorsList"
                        class="eligible-visitors-not-available">
                        <pendo-icon
                            type="eye-off"
                            size="22" />
                        <div class="eligible-visitors-not-available-text">
                            {{ $t('eligibleVisitorsList.Not enough eligible visitors') }}
                        </div>
                        <div class="eligible-visitors-not-available-description">
                            {{ $t('eligibleVisitorsList.To create this segment and view the data, it must include at least {minVisitorsInSegment} eligible visitors', { minVisitorsInSegment }) }}
                        </div>
                    </div>
                    <div
                        v-else
                        class="eligible-visitors-list">
                        <div
                            v-for="(visitor, index) in eligibleVisitors.visitorList"
                            :key="visitor"
                            class="eligible-visitors-list--visitor">
                            <div
                                class="eligible-visitors-list--visitor-link"
                                @mouseenter="visitorLinkMouseEnter(index)"
                                @mouseleave="visitorLinkMouseLeave">
                                <a
                                    v-if="hoverCardIndex !== index"
                                    :href="getVisitorDetailUrl(visitor)"
                                    target="_blank">
                                    {{ visitor }}
                                </a>
                                <!-- Rendering this component for each visitor causes performance issues, only render for visitor hovered over -->
                                <account-visitor-hover-card
                                    v-else
                                    :id="visitor"
                                    :disabled="hideAccountVisitorHoverCard"
                                    kind="visitor">
                                    <a
                                        :href="getVisitorDetailUrl(visitor)"
                                        target="_blank">
                                        {{ visitor }}
                                    </a>
                                </account-visitor-hover-card>
                            </div>
                            <pendo-divider height="5px" />
                        </div>
                    </div>
                    <div v-if="eligibleVisitors.count > eligibleVisitorsPreviewLimit">
                        {{
                            $t(
                                'eligibleVisitorsList.Previewing {eligibleVisitorsPreviewLimit} of {eligibleVisitorsCount} eligible visitors',
                                {
                                    eligibleVisitorsPreviewLimit: eligibleVisitorsPreviewLimit,
                                    eligibleVisitorsCount: eligibleVisitors.count,
                                },
                            )
                        }}
                    </div>
                </div>
            </div>
            <div
                v-else
                class="eligible-visitors-list--loading-indicator">
                <pendo-loading-indicator size="medium" />
            </div>
        </template>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { LOADING_STATES } from '@/constants';
import { connectAggregationToState, withComponent } from '@pendo/agg-connect';
import {
    PendoDivider,
    PendoIcon,
    PendoLoadingIndicator,
    PendoAlert,
    PendoMultiselect,
    PendoPopoverV2,
    PendoIconButton,
} from '@pendo/components';
import visitorList from '@/vuex/modules/segments/segment-eligible-visitor-list.aggregation';
import { getVisitorDetailUrl } from '@/visitor/utils';
import { formatNumberWithSuffix } from '@/utils/formatters';
import {
    eligibleVisitorsPreviewLimit,
    getIneligibleVisitorsTooltipMessage,
    getIneligibleVisitorsTooltipText,
} from '@/segments/constants';
import { validateDelayedAnalyticsWithSegmentFilters } from '@/segments/delayed-segment-csv.utils';
import AccountVisitorHoverCard from '@/common/account-visitor-hover-card/account-visitor-hover-card.vue';
import { mapGetters } from 'vuex';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

export default {
    name: 'EligibleVisitorsList',
    components: {
        PendoDivider,
        PendoMultiselect,
        PendoIcon,
        PendoLoadingIndicator,
        PendoAlert,
        AccountVisitorHoverCard,
        PendoPopoverV2,
        PendoIconButton,
    },
    props: {
        segment: {
            type: Object,
            default: null,
        },
        isValid: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['eligibleVisitorsLabel', 'eligibleVisitorsUpdated'],
    data () {
        return {
            analyticsDelayed: false,
            eligibleVisitorsPreviewLimit,
            livePreview: true,
            visitorCountAggSub: undefined,
            debouncedSegment: undefined,
            eligibleVisitors: {
                count: 0,
                isLoading: true,
                visitorList: [],
                error: false,
            },
            value: undefined,
            options: [],
            hasRules: true,
            eligibleVisitorsTooltipMessage: '',
            eligibleVisitorsTooltipText: '',
            filterTooltipMessage: '',
            hoverCardIndex: null,
            hoverCardTimeout: null,
        };
    },
    computed: {
        ...mapGetters({
            getFeatureFlag: 'subscription/getFeatureFlag',
            minVisitorsInSegment: 'subscription/minVisitorsInSegment',
        }),
        computedSegment () {
            if (isEmpty(this.segment)) {
                return null;
            }

            return { ...this.segment };
        },
        eligibleVisitorsLabel () {
            if (this.eligibleVisitors.error) {
                return this.$t('eligibleVisitorsList.Eligible visitors (--)');
            }
            if (this.hasRules && !this.eligibleVisitors.isLoading && this.eligibleVisitors.count) {
                return this.$t('eligibleVisitorsList.Eligible visitors ({eligibleVisitorsCount})', {
                    eligibleVisitorsCount: formatNumberWithSuffix(this.eligibleVisitors.count),
                });
            }

            return this.$t('eligibleVisitorsList.Eligible visitors');
        },
        timeRange () {
            return this.value.id === 'last-year' ? 'dateAdd(now(), -12, "months")' : '';
        },
        hasMinVisitorsRequirement () {
            return this.getFeatureFlag('enableMinVisitorsInSegment') && this.minVisitorsInSegment > 0;
        },
        showEligibleVisitorsList () {
            if (!this.hasMinVisitorsRequirement) return true;

            return this.eligibleVisitors.count >= this.minVisitorsInSegment;
        },
        hideAccountVisitorHoverCard () {
            return this.$productLine(PRODUCT_LINES.ADOPT);
        },
    },
    watch: {
        eligibleVisitors: {
            handler (newVal) {
                this.$emit('eligibleVisitorsUpdated', newVal);
            },
            deep: true,
        },
        'computedSegment': function (segment, prevSegment) {
            if (!this.isValid) return;
            if (isEmpty(segment)) return;
            if (segment.name !== prevSegment?.name) return;

            this.setHasRules(segment);
            if (!this.hasRules) {
                this.eligibleVisitors.isLoading = false;

                return;
            }

            this.debounceSegment(segment);
        },
        'segment.definition.filters': {
            async handler (filters) {
                const hasAnalyticsDelayedAtTimestamp = filters.some(
                    ({ analyticsDelayedAtTimestamp }) => analyticsDelayedAtTimestamp,
                );
                if (hasAnalyticsDelayedAtTimestamp) {
                    this.analyticsDelayed = await validateDelayedAnalyticsWithSegmentFilters(filters);
                }
            },
        },
    },
    created () {
        this.options = Object.freeze([
            { label: this.$t('eligibleVisitorsList.Last year visitors only'), id: 'last-year' },
            { label: this.$t('eligibleVisitorsList.All time visitors'), id: 'all-time' },
        ]);

        this.value = this.options.find(({ id }) => id === 'last-year');

        this.eligibleVisitorsTooltipMessage = getIneligibleVisitorsTooltipMessage();
        this.eligibleVisitorsTooltipText = getIneligibleVisitorsTooltipText();
        this.filterTooltipMessage = this.$t(
            "eligibleVisitorsList.Filtering eligible visitors helps you find the most relevant ones and improves system performance. It doesn't affect the actual number of eligible visitors. Visitors who were manually uploaded but haven't logged in won't appear until they use your app or their metadata is updated.",
        );

        this.setHasRules(this.computedSegment);
        if (!this.hasRules) {
            this.eligibleVisitors.isLoading = false;
        }
        this.setupVisitorCountAgg();
        this.debounceSegment = debounce((segment) => {
            this.setDebouncedSegment(segment);
        }, 1500);
    },
    beforeUnmount () {
        this.visitorCountAggSub.unsubscribe();
    },
    methods: {
        getVisitorDetailUrl,
        loadingStatus () {
            this.eligibleVisitors.isLoading = true;
            this.eligibleVisitors.error = false;
        },
        rejectedStatus () {
            this.eligibleVisitors.count = 0;
            this.eligibleVisitors.isLoading = false;
            this.eligibleVisitors.error = true;
        },
        resolvedStatus (value) {
            this.eligibleVisitors.count = value[0].eligibleVisitorCount;
            this.eligibleVisitors.visitorList = value[0].visitorList || [];
            this.eligibleVisitors.isLoading = false;
        },
        setupVisitorCountAgg () {
            const agg$ = connectAggregationToState(
                visitorList,
                withComponent({ segment: 'debouncedSegment', livePreview: 'livePreview', timeRange: 'timeRange' }),
            )({ component: this });

            this.visitorCountAggSub = agg$.subscribe(async ({ status, value }) => {
                if (status === LOADING_STATES.LOADING) this.loadingStatus();
                if (status === LOADING_STATES.REJECTED) this.rejectedStatus();
                if (status === LOADING_STATES.RESOLVED) this.resolvedStatus(value);
                this.setHasRules(this.computedSegment);
                this.$emit('eligibleVisitorsLabel', this.eligibleVisitorsLabel);
            });
        },
        setDebouncedSegment (segment) {
            this.eligibleVisitors.isLoading = false;
            this.debouncedSegment = segment;
        },
        setHasRules (segment) {
            const rulesArr = segment?.definition?.filters || [];
            const someValidRule = rulesArr.some((rule) => rule.$valid);
            const someValidOrRule = rulesArr.some((rule) => rule.or?.some((orRule) => orRule.$valid));

            this.hasRules = someValidRule || someValidOrRule;
        },
        visitorLinkMouseEnter (index) {
            clearTimeout(this.hoverCardTimeout);
            this.hoverCardIndex = index;
        },
        visitorLinkMouseLeave () {
            this.hoverCardTimeout = setTimeout(() => {
                this.hoverCardIndex = null;
            }, 200);
        },
    },
};
</script>

<style scoped lang="less">
.eligible-visitors {
    &-wrapper {
        width: 100%;
    }

    &-analytics-delayed {
        margin-bottom: 12px;
    }

    &-description {
        font-size: 14px;
        font-weight: 400;
        line-height: 150%;
        color: var(--palette-gray-70);
    }

    &-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        &--visitor-link {
            padding-bottom: 5px;
            a {
                overflow-wrap: anywhere;
            }
        }
        &--loading-indicator {
            margin-top: 24px;
        }
    }
    &-filter-wrapper {
        display: flex;
        gap: 2px;
        align-items: center;
        padding-top: 16px;
        padding-bottom: 4px;
    }

    &-label {
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 22px;
    }
    &-text {
        display: flex;
        gap: 2px;
        align-items: center;
        padding-top: 16px;
        padding-bottom: 12px;
        &.bold {
            font-weight: 600;
        }
    }
    &-info {
        display: flex;
        align-items: center;
        gap: 2px;
        font-weight: 600;
    }

    &-time-range-select {
        display: block;
        margin-bottom: 24px;
    }

    &-not-available {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-top: 64px;

        &-text {
            font-size: 14px;
            font-weight: 600;
            line-height: 150%;
            margin-top: 8px;
        }

        &-description {
            font-size: 12px;
            font-weight: 400;
            line-height: 150%;
            text-align: center;
        }
    }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.eligible-visitors-info-tooltip {
    font-weight: 400;
    font-size: 12px;
    margin-left: 8px;
    margin-right: 8px;

    ul {
        padding-inline-start: 20px;
        margin: 0;
    }

    .tooltip-inner {
        max-width: unset !important;
        ul {
            padding-inline-start: 20px;
            margin: 0;
        }
    }
}

.eligible-visitors-filter-tooltip {
    font-weight: 400;
    font-size: 12px;
    margin-left: 8px;
    margin-right: 8px;
    max-width: 300px;
}
</style>
